import { useContext, useEffect, useRef, useState } from "react";
import { EnteredContext } from "../EnteredContext";

function prefersReduced() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Small supporting text that "loads": first cycles through dots ( . .. ... ),
 * then types the real text in. Runs when scrolled into view (after the splash),
 * and re-runs whenever `text` changes (e.g. the timeline count on filtering).
 * Respects prefers-reduced-motion.
 */
export default function DotsText({ text, as: Tag = "span", className = "" }) {
  const [display, setDisplay] = useState(() => (prefersReduced() ? text : ""));
  const ref = useRef(null);
  const ranOnce = useRef(false);
  const ready = useContext(EnteredContext);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready) return undefined;

    if (prefersReduced()) {
      setDisplay(text);
      return undefined;
    }

    const dotsDur = 650;
    const typeDur = Math.min(700, Math.max(200, text.length * 26));
    const total = dotsDur + typeDur;

    let raf;
    const run = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = now - start;
        let out;
        if (t < dotsDur) {
          out = ".".repeat((Math.floor(t / 200) % 3) + 1);
        } else if (t < total) {
          const p = (t - dotsDur) / typeDur;
          out = text.slice(0, Math.floor(p * text.length));
        } else {
          out = text;
        }
        setDisplay(out);
        if (t < total) raf = requestAnimationFrame(tick);
        else setDisplay(text);
      };
      raf = requestAnimationFrame(tick);
    };

    // First time: wait until scrolled into view. After that: re-run immediately
    // whenever the text changes (already on screen).
    if (ranOnce.current) {
      run();
      return () => {
        if (raf) cancelAnimationFrame(raf);
      };
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !ranOnce.current) {
          ranOnce.current = true;
          run();
          io.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, ready]);

  return (
    <Tag ref={ref} className={className + " hud-scramble"}>
      <span className="hud-scramble__ghost" aria-hidden="true">
        {text}
      </span>
      <span className="hud-scramble__layer">{display}</span>
    </Tag>
  );
}
